import { useCallback, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  historySelectionAtom,
  historySelectedItemsListAtom,
  clearHistorySelectionAtom,
} from '@renderer/overlay/Sidepanel/stores/history-select'
import { useBulkDeleteChats } from '@renderer/hooks/chat-history'
import { useBulkDeleteAudioNotes } from '@renderer/hooks/audio-notes'
import { useDeleteStagedActions } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

export function useHistoryBulkDelete() {
  const selectedItems = useAtomValue(historySelectedItemsListAtom)
  const setSelection = useSetAtom(historySelectionAtom)
  const clearSelection = useSetAtom(clearHistorySelectionAtom)
  const { mutateAsync: bulkDeleteChats, isPending: isDeletingChats } = useBulkDeleteChats()
  const { mutateAsync: bulkDeleteAudioNotes, isPending: isDeletingAudioNotes } = useBulkDeleteAudioNotes()
  const { bulkDeleteStagedActions } = useDeleteStagedActions()
  const [isDeletingActions, setIsDeletingActions] = useState(false)

  const isDeleting = isDeletingChats || isDeletingAudioNotes || isDeletingActions

  const handleDelete = useCallback(async () => {
    const chatIds = selectedItems.filter((i) => i.type === 'chat').map((i) => i.id)
    const audioNoteIds = selectedItems.filter((i) => i.type === 'audio-note').map((i) => i.id)
    const actionIds = selectedItems.filter((i) => i.type === 'completed-action').map((i) => i.id)

    const promises: Array<Promise<unknown>> = []
    if (chatIds.length > 0) promises.push(bulkDeleteChats(chatIds))
    if (audioNoteIds.length > 0) promises.push(bulkDeleteAudioNotes(audioNoteIds))
    if (actionIds.length > 0) {
      setIsDeletingActions(true)
      promises.push(bulkDeleteStagedActions(actionIds).finally(() => setIsDeletingActions(false)))
    }

    const results = await Promise.allSettled(promises)

    // Build set of IDs that were successfully deleted
    const deletedIds = new Set<string>()
    let promiseIndex = 0
    if (chatIds.length > 0) {
      if (results[promiseIndex]?.status === 'fulfilled') chatIds.forEach((id) => deletedIds.add(id))
      promiseIndex++
    }
    if (audioNoteIds.length > 0) {
      if (results[promiseIndex]?.status === 'fulfilled') audioNoteIds.forEach((id) => deletedIds.add(id))
      promiseIndex++
    }
    if (actionIds.length > 0) {
      if (results[promiseIndex]?.status === 'fulfilled') actionIds.forEach((id) => deletedIds.add(id))
      promiseIndex++
    }

    // History invalidation happens inside each delete mutation (chat:history:invalidate,
    // audio-notes:changed, staged-action delete) — this hook only reconciles selection.

    // Remove successfully deleted items from selection; clear entirely if all succeeded
    if (deletedIds.size > 0) {
      if (deletedIds.size === selectedItems.length) {
        clearSelection()
      } else {
        setSelection((current) => {
          const next = new Map(current)
          for (const [key, item] of current) {
            if (deletedIds.has(item.id)) next.delete(key)
          }
          return next
        })
      }
    }
  }, [selectedItems, bulkDeleteChats, bulkDeleteAudioNotes, bulkDeleteStagedActions, clearSelection, setSelection])

  return { handleDelete, isDeleting }
}
